// Library methods
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// MUI Components
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";

// Components
import ClinicStats from "../../containers/Dashboard/ClinicStats";
import DashboardDevicesList from "../../containers/Dashboard/DashboardDevicesList";
import RecentlyCompletedList from "../../containers/Dashboard/RecentlyCompletedList";
import TodayExamsList from "../../containers/Dashboard/TodayExamsList";

// Styles
import { PageContainerWrapper } from "../../styles/muiStylesHelper";

// Utilities
import useClinic from "../../hooks/useClinic";
import { getPatients } from "../../services/Patient";
import {
  getClinicExamStatistics,
  getClinicLinkedExams,
  getClinicPatientStatistics,
  getClinicRecentlyCompletedExams,
  getClinicScheduledExams,
} from "../../services/Exam";
import { getClinicDevices } from "../../services/Device";
import { useSignalR } from "../../hooks/useSignalR";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { getUtcStartEndDate } from "../../utils/dateHelper";
import { useDeviceAppVersion } from "../../contexts/DeviceContext";
import { LiveUpdateStatusProvider } from "../../contexts/LiveUpdateStatusContext";
import { ExamListProvider } from "../../contexts/ExamListContext";

const Patients = () => {
  // internationalization
  const { t } = useTranslation();

  // navigate
  const navigate = useNavigate();

  // auth0
  const { getAccessTokenSilently } = useAuth0();

  // clinic context
  const { clinicId, clinicName, clinicTimezone } = useClinic();

  // states
  const [devices, setDevices] = useState([]);
  const [patients, setPatients] = useState([]);
  const [todayExams, setTodayExams] = useState(null);
  const [recentlyCompletedExams, setRecentlyCompletedExams] = useState(null);
  const [staticsData, setStaticsData] = useState(null);
  const [devicesOptions, setDevicesOptions] = useState([]);

  // indicates if the data has been fetched, or if it is still being fetched
  const [isLoadingDevices, setIsLoadingDevices] = useState(true);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(true);
  const [isLoadingSignalR, setIsLoadingSignalR] = useState(false);
  const [isLoadingRecentlyCompleted, setIsLoadingRecentlyCompleted] =
    useState(false);
  const [isLoadingTodayExams, setIsLoadingTodayExams] = useState(false);

  // signalR related states
  const [devicesWithExams, setDevicesWithExams] = useState([]);

  const clinicStatsRef = useRef(null);

  const [term, setTerm] = useState(0);

  useDeviceAppVersion(devices);

  const handleDeviceListUpdated = useCallback(async (data) => {
    // get all devices
    const devicesData = JSON.parse(data);

    // get the devices from this clinic with associated exams
    const associatedDevicesList = devicesData?.data?.filter(
      (device) => device.associatedExam !== undefined
    );
    const devicesWithoutExams = devicesData?.data?.filter(
      (device) => device.associatedExam === undefined
    );
    setDevices(devicesData?.data ? [...devicesData?.data] : []);
    setDevicesWithExams([...associatedDevicesList]);
    setDevicesOptions([...devicesWithoutExams]);
  }, []);

  // signalR setup
  const { connection, reconnectedRef } = useSignalR(
    clinicId,
    handleDeviceListUpdated,
    setIsLoadingSignalR
  );

  // online status
  const isOnline = useOnlineStatus(connection, clinicId);

  useEffect(() => {
    if (isOnline) {
      if (!reconnectedRef.current) return;
      const timeoutId = setTimeout(() => {
        setIsLoadingSignalR(false);
      }, 2000); // Delay setting the state by 2 sec to sync with signalR reconnection

      return () => clearTimeout(timeoutId);
    } else setIsLoadingSignalR(true);
  }, [isOnline, reconnectedRef]);

  // method that fetches patients data
  const fetchPatients = useCallback(
    async (signal) => {
      if (!clinicName) navigate("/invalid");
      try {
        // get authToken
        const token = await getAccessTokenSilently();

        // get patients list
        const patientsList = await getPatients(token, clinicId, signal);
        setPatients(patientsList);
      } catch (e) {
        //console.log(e);
      }
    },
    [clinicId, clinicName, getAccessTokenSilently, navigate]
  );

  // method that does the patients data fetching, showing the loading indicator
  const fetchPatientsCall = useCallback(
    async (signal) => {
      try {
        await fetchPatients(signal);
      } catch (e) {
        //console.log(e);
      } finally {
      }
    },
    [fetchPatients]
  );

  // method that fetches devices data
  const fetchDevices = useCallback(
    async (signal) => {
      if (!clinicName) navigate("/invalid");
      try {
        // get authToken
        const token = await getAccessTokenSilently();

        // get the devices from this clinic
        const devicesData = await getClinicDevices(token, clinicId, signal);

        // devices without an associated exam
        const devicesWithoutExam = devicesData.filter(
          (device) => device.associatedExam === undefined
        );

        // devices with an associated exam
        const devicesWithExam = devicesData.filter(
          (device) => device.associatedExam !== undefined
        );

        // set the states
        setDevices([...devicesData]);
        setDevicesOptions([...devicesWithoutExam]);
        setDevicesWithExams([...devicesWithExam]);
      } catch (e) {
        //console.log(e);
      }
    },
    [clinicId, clinicName, getAccessTokenSilently, navigate]
  );

  // method that does the devices data fetching, showing the loading indicator
  const fetchDevicesCall = useCallback(
    async (signal) => {
      try {
        setIsLoadingDevices(true);
        await fetchDevices(signal);
      } catch (e) {
        //console.log(e);
      } finally {
        setIsLoadingDevices(false);
      }
    },
    [fetchDevices]
  );

  // method that fetches exams data
  const fetchDashboard = useCallback(
    async (signal) => {
      if (!clinicName) navigate("/invalid");
      try {
        // get authToken
        const token = await getAccessTokenSilently();

        if (!Array.isArray(todayExams)) setIsLoadingTodayExams(true);
        if (!Array.isArray(recentlyCompletedExams))
          setIsLoadingRecentlyCompleted(true);
        const todayUtc = getUtcStartEndDate(0, clinicTimezone);
        const commonPayload = {
          startDate: todayUtc.startOfUTC,
          endDate: todayUtc.endOfUTC,
        };
        const timeRangeUtc = getUtcStartEndDate(term, clinicTimezone);
        const recentFilterPayload = {
          recentFilterDate: timeRangeUtc.startOfUTC,
        };
        const promises = [
          getClinicScheduledExams(token, clinicId, commonPayload, signal),
          getClinicLinkedExams(token, clinicId, commonPayload, signal),
          getClinicRecentlyCompletedExams(
            token,
            clinicId,
            recentFilterPayload,
            signal
          ),
          getClinicPatientStatistics(
            token,
            clinicId,
            recentFilterPayload,
            signal
          ),
          getClinicExamStatistics(token, clinicId, recentFilterPayload, signal),
        ];

        const [
          scheduledExamData,
          linkedExamData,
          recentlyCompletedExamsData,
          patientStatistics,
          examStatistics,
        ] = await Promise.all(promises);
        setStaticsData({
          ...patientStatistics,
          ...examStatistics,
        });

        // const formatExams = useCallback(
        const formatExams = (exams) => {
          const getExamCompletedDate = (exam) => {
            // get all section keys where at least 1 exam is complete
            const examSectionsKeys = Object.keys(exam).filter((key) =>
              key.includes("Sections")
            );
            const completedExamSectionKeys = examSectionsKeys?.filter(
              (key) =>
                exam[key]?.length > 0 &&
                exam[key]?.some((examSection) =>
                  Boolean(examSection.completionDate)
                )
            );

            // if no exam sections are complete, return null
            if (!completedExamSectionKeys?.length) {
              return null;
            }

            // flatten the completed exam sections into 1 array
            // sort the latest completionDate to be first
            const completedExamSections = completedExamSectionKeys
              ?.map((sectionName) => exam[sectionName])
              ?.flat()
              ?.sort((a, b) =>
                (b?.completionDate).localeCompare(a?.completionDate)
              );

            // return the latest completionDate
            return completedExamSections?.[0]?.completionDate;
          };

          return exams?.map((exam) => {
            const completedDate = getExamCompletedDate(exam);
            return {
              ...exam,
              isCompleted: completedDate ? true : false,
              completedDate: completedDate,
              firstName: exam.patient.firstName,
              lastName: exam.patient.lastName,
              // adding some helpful information to the exam
              tutorialOrder: exam.requiresTutorial
                ? t("word_yes")
                : t("word_no"),
              deviceName: exam.associatedDevice?.device?.alias || "-",
              // manually set the type for multi-exam packages
              type:
                exam?.sectionTypeOrder?.length > 1
                  ? t("word_multi_exam_package")
                  : exam?.type,
            };
          });
        };
        const formattedTodayExams = formatExams(
          scheduledExamData?.length ? scheduledExamData : []
        );
        if (linkedExamData?.length) {
          linkedExamData.forEach((linkedExam) => {
            if (
              !formattedTodayExams.find((exam) => exam.id === linkedExam.id)
            ) {
              formattedTodayExams.push(linkedExam);
            }
          });
        }

        const formattedRecentlyCompletedExams = formatExams(
          recentlyCompletedExamsData?.length ? recentlyCompletedExamsData : []
        );

        // set the states
        setTodayExams(formattedTodayExams);
        setRecentlyCompletedExams(formattedRecentlyCompletedExams);
      } catch (e) {
        setIsLoadingRecentlyCompleted(false);
        setIsLoadingTodayExams(false);
        console.log(e);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      clinicId,
      clinicName,
      getAccessTokenSilently,
      navigate,
      t,
      term,
      todayExams?.length,
    ]
  );

  // method that does the exams data fetching, showing the loading indicator
  const fetchDashboardCall = useCallback(
    async (signal) => {
      try {
        await fetchDashboard(signal);
      } catch (e) {
        //console.log(e);
      }
    },
    [fetchDashboard]
  );

  useEffect(() => {
    setIsLoadingDashboard(
      !todayExams && !recentlyCompletedExams && !staticsData
    );
  }, [recentlyCompletedExams, staticsData, todayExams]);

  // initial data fetching
  useEffect(() => {
    const controller = new AbortController();
    fetchPatientsCall(controller.signal);
    fetchDevicesCall(controller.signal);
    fetchDashboardCall(controller.signal);
    return () => {
      controller.abort();
    };
  }, [fetchPatientsCall, fetchDevicesCall, fetchDashboardCall]);

  useEffect(() => {
    if (Array.isArray(todayExams)) setIsLoadingTodayExams(false);
  }, [todayExams]);

  useEffect(() => {
    if (Array.isArray(recentlyCompletedExams))
      setIsLoadingRecentlyCompleted(false);
  }, [recentlyCompletedExams]);

  useEffect(() => {
    setRecentlyCompletedExams(null);
    setIsLoadingRecentlyCompleted(true);
  }, [term]);

  const isLoadingExams = useMemo(
    () => isLoadingRecentlyCompleted || isLoadingTodayExams,
    [isLoadingRecentlyCompleted, isLoadingTodayExams]
  );

  return (
    <LiveUpdateStatusProvider devicesWithExams={devicesWithExams}>
      <Box
        sx={() => PageContainerWrapper()}
        px="24px"
        backgroundColor="#E5E9ED70"
      >
        <Grid
          container
          width="100%"
          display="flex"
          sx={{
            flexDirection: {
              xs: "column",
              md: "row",
            },
          }}
        >
          <Grid
            container
            item
            display="flex"
            flexDirection="column"
            maxWidth={{ xs: "100%", md: "300px" }}
            pt="24px"
            gap="24px"
          >
            <ClinicStats
              staticsData={staticsData}
              isLoading={isLoadingDashboard}
              ref={clinicStatsRef}
              term={term}
            />
            <DashboardDevicesList
              rows={devices}
              isLoading={isLoadingDevices || isLoadingExams}
              devicesWithExams={devicesWithExams}
              patients={patients}
              connection={connection}
            />
          </Grid>
          <Grid
            item
            display={{ xs: "none", md: "flex" }}
            justifyContent="center"
            width="3px"
            mx="20px"
          >
            <Divider
              sx={{
                borderColor: "#E2772E90",
                height: "100%",
                borderRightWidth: 3,
                borderBottomWidth: 0,
                width: "3px",
              }}
            />
          </Grid>
          <Grid
            id="dashboard-exams-container"
            display="flex"
            flexGrow={1}
            flexDirection="column"
            height="fit-content"
            mt="24px"
          >
            <Typography
              fontSize="24px"
              sx={{
                fontWeight: "bold",
                color: "#E2772E",
                mb: 0,
              }}
            >
              {t("exams_title")}
            </Typography>
            <Grid
              display="flex"
              gap="12px"
              width="100%"
              sx={{
                flexDirection: {
                  xs: "column",
                  xl: "row",
                },
              }}
            >
              <Grid item mt="16px" width={{ xs: "100%", xl: "40%" }}>
                <RecentlyCompletedList
                  rows={recentlyCompletedExams}
                  associatedExamDevices={devicesWithExams}
                  isLoading={isLoadingRecentlyCompleted}
                  term={term}
                  setTerm={setTerm}
                />
              </Grid>
              <Grid
                item
                mt={{ xs: "100px", xl: "16px" }}
                width={{ xs: "100%", xl: "60%" }}
              >
                <ExamListProvider
                  devicesWithExams={devicesWithExams}
                  devicesOptions={devicesOptions}
                  refetchExams={fetchDashboard}
                  rows={todayExams}
                  setRows={setTodayExams}
                >
                  <TodayExamsList
                    connection={connection}
                    isLoading={isLoadingTodayExams}
                    patients={patients}
                    refetchDevices={fetchDevices}
                    isLoadingSignalR={isLoadingSignalR}
                  />
                </ExamListProvider>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </LiveUpdateStatusProvider>
  );
};

export default Patients;
